'use client';
import { BlocksContent, BlocksRenderer } from '@strapi/blocks-react-renderer';
import Markdown from 'markdown-to-jsx';
import React from 'react';

import { cn } from '@/lib/utils';

import { BlockComponentModel } from '@/components/cms/types/blok-component';

import { CodeBlock, CodeBlockProps } from '@/decodo/blocks/CodeBlock';
import Heading, { HeadingProps } from '@/decodo/components/atoms/Heading';
import NextImage, { NextImageProps } from '@/decodo/components/atoms/Image';
import LinkButton, {
  LinkButtonProps,
} from '@/decodo/components/atoms/LinkButton';

import { buildMarkdownOptions } from './buildMarkdownOptions';

export type RichTextFieldStrapiProps = {
  text?: BlocksContent;
  text2?: string;
};

interface RichTextFieldBaseProps {
  classNames?: {
    paragraph?: string;
    listOrdered?: string;
    listUnordered?: string;
    quote?: string;
    image?: string;
    code?: CodeBlockProps['classNames'];
    heading?: {
      h1?: HeadingProps['classNames'];
      h2?: HeadingProps['classNames'];
      h3?: HeadingProps['classNames'];
      h4?: HeadingProps['classNames'];
      h5?: HeadingProps['classNames'];
      h6?: HeadingProps['classNames'];
      xl?: HeadingProps['classNames'];
      l?: HeadingProps['classNames'];
    };
    link?: LinkButtonProps['classNames'];
  };
  extraProps?: {
    paragraph?: React.HTMLAttributes<HTMLParagraphElement>;
    listOrdered?: React.OlHTMLAttributes<HTMLOListElement>;
    listUnordered?: React.HTMLAttributes<HTMLUListElement>;
    quote?: React.BlockquoteHTMLAttributes<HTMLQuoteElement>;
    image?: NextImageProps;
    code?: CodeBlockProps;
    heading?: {
      h1?: HeadingProps;
      h2?: HeadingProps;
      h3?: HeadingProps;
      h4?: HeadingProps['classNames'];
      h5?: HeadingProps['classNames'];
      h6?: HeadingProps['classNames'];
    };
    link?: LinkButtonProps;
  };
}

export type RichTextFieldProps = BlockComponentModel<RichTextFieldStrapiProps> &
  RichTextFieldBaseProps;

const retainContextSizeClass = 'text-base';
// Helper function to convert escaped HTML entities back to their original characters.
// For example, it turns '&lt;sup&gt;' into '<sup>'
function unescapeHtml(html: string): string {
  return html
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&copy;/g, '©')
    .replace(/&reg;/g, '®');
}

export default function RichTextFieldStrapi({
  block,
  extraProps,
  classNames,
}: RichTextFieldProps) {
  if (block?.text2) {
    const unescapedText2 = unescapeHtml(block.text2);

    return (
      <Markdown options={buildMarkdownOptions(classNames, extraProps)}>
        {unescapedText2}
      </Markdown>
    );
  }

  if (!block?.text) return;

  return (
    <BlocksRenderer
      content={block.text}
      blocks={{
        paragraph: ({ children }) => (
          <p
            className={cn(classNames?.paragraph)}
            {...extraProps?.paragraph}
            data-tracker-context='rich-text-field-paragraph'
          >
            {children}
          </p>
        ),
        list: ({ children, format }) => {
          const baseClasses = 'flex gap-5 flex-col ml-5 list-outside text-left';

          if (format === 'ordered') {
            return (
              <ol
                className={cn(
                  baseClasses,
                  'list-decimal',
                  classNames?.listOrdered
                )}
                data-tracker-context='rich-text-field-list-ordered'
                {...extraProps?.listOrdered}
              >
                {children}
              </ol>
            );
          }

          return (
            <ul
              className={cn(
                baseClasses,
                'list-disc',
                classNames?.listUnordered
              )}
              data-tracker-context='rich-text-field-list-unordered'
              {...extraProps?.listUnordered}
            >
              {children}
            </ul>
          );
        },
        quote: ({ children }) => (
          <blockquote
            className={cn('border-l-2 border-gray-300 pl-4', classNames?.quote)}
            data-tracker-context='rich-text-field-quote'
            {...extraProps?.quote}
          >
            {children}
          </blockquote>
        ),
        // TODO implement code
        code: ({ plainText }) => (
          <CodeBlock
            items={[
              {
                code: plainText,
                language: 'Python',
              },
            ]}
            data-tracker-context='rich-text-field-code'
            {...extraProps?.code}
          />
        ),
        image: ({ image }) => {
          return (
            <NextImage
              src={image.url}
              alt={image.alternativeText || ''}
              width={image.width}
              className={cn(classNames?.image)}
              data-tracker-context='rich-text-field-image'
              {...extraProps?.image}
            />
          );
        },
        heading: ({ children, level }) => {
          switch (level) {
            case 1:
              return (
                <Heading
                  level='h1'
                  classNames={{
                    root: cn('w-full', classNames?.heading?.h1?.root),
                  }}
                  data-tracker-context='rich-text-field-heading-h1'
                  {...extraProps?.heading?.h1}
                >
                  {children}
                </Heading>
              );
            case 2:
              return (
                <Heading
                  level='h2'
                  classNames={{
                    root: cn('w-full', classNames?.heading?.h2?.root),
                  }}
                  data-tracker-context='rich-text-field-heading-h2'
                  {...extraProps?.heading?.h2}
                >
                  {children}
                </Heading>
              );
            case 3:
              return (
                <Heading
                  level='h3'
                  classNames={{
                    root: cn('w-full', classNames?.heading?.h3?.root),
                  }}
                  data-tracker-context='rich-text-field-heading-h3'
                  {...extraProps?.heading?.h3}
                >
                  {children}
                </Heading>
              );
            case 4:
              return (
                <Heading
                  level='h4'
                  classNames={{
                    root: cn('w-full', classNames?.heading?.h4?.root),
                  }}
                  data-tracker-context='rich-text-field-heading-h4'
                  {...extraProps?.heading?.h4}
                >
                  {children}
                </Heading>
              );
            case 5:
              return (
                <Heading
                  level='h5'
                  classNames={{
                    root: cn('w-full', classNames?.heading?.h5?.root),
                  }}
                  data-tracker-context='rich-text-field-heading-h5'
                  {...extraProps?.heading?.h5}
                >
                  {children}
                </Heading>
              );
            default:
              return (
                <Heading
                  level='h6'
                  classNames={{
                    root: cn('w-full', classNames?.heading?.h6?.root),
                  }}
                  data-tracker-context='rich-text-field-heading-h6'
                  {...extraProps?.heading?.h6}
                >
                  {children}
                </Heading>
              );
          }
        },
        link: ({ children, url }) => {
          const classNameStyles = cn('hover:underline word-break');

          return (
            <LinkButton
              href={url}
              classNames={{
                ...classNames?.link,
                root: cn(
                  classNameStyles,
                  retainContextSizeClass,
                  classNames?.link?.root
                ),
                inner: 'whitespace-normal',
              }}
              variant='text'
              color='tertiary'
              slim
              data-tracker-context='rich-text-field-link'
              {...extraProps?.link}
            >
              {children}
            </LinkButton>
          );
        },
      }}
      modifiers={{
        bold: ({ children }) => (
          <strong
            className={cn('bold')}
            data-tracker-context='rich-text-field-bold'
          >
            {children}
          </strong>
        ),
        italic: ({ children }) => (
          <span
            className={cn('italic')}
            data-tracker-context='rich-text-field-italic'
          >
            {children}
          </span>
        ),
        underline: ({ children }) => (
          <span
            className={cn('underline')}
            data-tracker-context='rich-text-field-underline'
          >
            {children}
          </span>
        ),
        strikethrough: ({ children }) => (
          <span
            className={cn('line-through')}
            data-tracker-context='rich-text-field-strikethrough'
          >
            {children}
          </span>
        ),
        code: ({ children }) => (
          <code data-tracker-context='rich-text-field-code'>{children}</code>
        ),
      }}
    />
  );
}
